import { useIntl } from '@weenat/wintl';
import isArray from 'lodash-es/isArray';
import isEmpty from 'lodash-es/isEmpty';
import isNil from 'lodash-es/isNil';
import moment from 'moment-timezone';
import { useCallback, useMemo, useState } from 'react';
import ErrCode from '../ErrCode';
import { OptionCode } from '../enums';
import { useCropTracking } from '../hooks';
import { mergeMutationState, mergeQueryState } from '../mergeState';
import { InfectionEvolutionStatus, InfectionLevel } from '../resources/tavmills';
import useMutation from '../useMutation';
import useQuery from '../useQuery';
import { useClient } from './../ClientProvider';
import { useCanActivateOptionOnResource } from './options';
import { isPlot, isStation } from './utils/resourcesTypeCheckers';
/** Extract different cases from request errors */
const getTavmillsErrors = (req) => {
    // can be a Record of BackendErrors or Record of Record of BackendErrors
    // flattening to only get BackendErrors
    const fieldErrors = !isNil(req.error) && !isNil(req.error.fieldErrors)
        ? Object.entries(req.error.fieldErrors).reduce((acc, [, value]) => {
            if (isArray(value)) {
                acc.push(...value);
            }
            else {
                const errorsFromObj = Object.entries(value).reduce((errors, [, errorValue]) => {
                    errors.push(...errorValue);
                    return errors;
                }, []);
                acc.push(...errorsFromObj);
            }
            return acc;
        }, [])
        : [];
    const nonFieldErrors = !isNil(req.error) && !isNil(req.error.nonFieldErrors) ? req.error.nonFieldErrors : [];
    // Overkill but error type has changed so much I want to be certain to catch the code
    const isConfigError = nonFieldErrors.some((e) => e.code === ErrCode.tavmillsOadConfiguration) ||
        fieldErrors.some((e) => e.code === ErrCode.tavmillsOadConfiguration);
    const isObligatoryStepNotReachedError = nonFieldErrors.some((e) => e.code === ErrCode.tavmillsOadStageNotActivated) ||
        fieldErrors.some((e) => e.code === ErrCode.tavmillsOadStageNotActivated);
    const isUnknownError = !isEmpty(fieldErrors) ||
        nonFieldErrors.some((e) => e.code !== ErrCode.tavmillsOadConfiguration &&
            e.code !== ErrCode.tavmillsOadStageNotActivated);
    return { isConfigError, isObligatoryStepNotReachedError, isUnknownError };
};
export const useTavmills = ({ plot, isFull }) => {
    const client = useClient();
    const { lastCrop, isLoading: isCropTrackingLoading } = useCropTracking({
        plot
    });
    const episodesRequest = useQuery(client.tavmills.results({ cropId: lastCrop?.id }), {
        enabled: !isNil(lastCrop?.id)
    });
    const episodes = episodesRequest.data ? episodesRequest.data : [];
    const ASESReportsRequest = useQuery(client.measurementsReports.getAllPages({
        plotId: plot.id,
        metrics: ['ASES']
    }));
    const [lastEpisode, ...remainingEpisodes] = episodes;
    const reports = ASESReportsRequest.data;
    const hasCurrentEpisode = lastEpisode?.status === InfectionEvolutionStatus.current;
    const allPreviousEpisodes = hasCurrentEpisode ? remainingEpisodes : episodes;
    const previousEpisodesDisplayed = isFull
        ? allPreviousEpisodes
        : !isEmpty(allPreviousEpisodes)
            ? [allPreviousEpisodes[0]]
            : [];
    const horizontalSpacing = isFull ? 4 : 0;
    const previousMajorEpisodes = isFull
        ? previousEpisodesDisplayed.filter((x) => x.level !== InfectionLevel.none)
        : previousEpisodesDisplayed;
    const mergedRequestsState = mergeQueryState(episodesRequest, ASESReportsRequest);
    const isLoading = isCropTrackingLoading || mergedRequestsState.isLoading;
    return {
        hasCurrentEpisode,
        horizontalSpacing,
        isLoading,
        // If we have no episode, there is no last episode
        lastEpisode: lastEpisode,
        previousMajorEpisodes,
        reports
    };
};
const APPLE_SEED_ID = 2;
const BUD_BURST_STAGE_ID = 74;
/**
 * Hook to handle Tavmills activation and other automations
 */
export const useTavmillsConfig = ({ plot }) => {
    const client = useClient();
    const today = moment();
    const startOfTheYear = today.clone().startOf('y').unix().toString();
    const firstOfMatch = today.clone().set('month', 2).startOf('month');
    const isAfterFirstOfMarch = today.isAfter(firstOfMatch);
    const { hasNoActiveCrop, hasNoActiveCycle, isLoading: isCropTrackingLoading, isSkippingStages, lastCrop, skipToStage, stages } = useCropTracking({
        plot
    });
    const [createCycle, createCycleRequest] = useMutation(client.cycles.create(lastCrop?.id)());
    const [createCrop, createCropRequest] = useMutation(client.crops.create(), {
        onSuccess: (data) => createCycle({ cropId: data.id, startedAt: startOfTheYear })
    });
    const createAppleSeedTracking = useCallback(() => {
        createCrop({ plotId: plot.id, seedId: APPLE_SEED_ID });
    }, [createCrop, plot.id]);
    const [updateCrop, updateCropRequest] = useMutation(client.crops.update(lastCrop?.id), {
        onSuccess: () => createAppleSeedTracking()
    });
    const tavmillsDssRequest = useQuery(client.tavmills.results({ cropId: lastCrop?.id }), {
        enabled: !isNil(lastCrop?.id)
    });
    const { isConfigError, isObligatoryStepNotReachedError, isUnknownError } = useMemo(() => getTavmillsErrors(tavmillsDssRequest), [tavmillsDssRequest]);
    const isLastCycleEmpty = stages.every((x) => x.date === null);
    const appleSeedTrackingCreationState = mergeMutationState(createCycleRequest, createCropRequest);
    const isAtLeastOneRequestFetching = isCropTrackingLoading ||
        appleSeedTrackingCreationState.isPending ||
        tavmillsDssRequest.isLoading;
    const skipToBudBurstStage = useCallback(async () => {
        await skipToStage(BUD_BURST_STAGE_ID).then(() => {
            tavmillsDssRequest.refetch();
        });
    }, [skipToStage]);
    const archiveCropAndRestartCycle = useCallback(() => {
        updateCrop({ isArchived: true });
    }, [updateCrop]);
    return {
        appleSeedTrackingCreationState,
        archiveCropAndRestartCycle,
        archiveCropAndRestartCycleRequest: updateCropRequest,
        createAppleSeedTracking,
        hasNoActiveCrop,
        hasNoActiveCycle,
        isAfterFirstOfMarch,
        isAtLeastOneRequestFetching,
        isConfigError,
        isLastCycleEmpty,
        isObligatoryStepNotReachedError,
        isSkippingStages,
        isUnknownError,
        skipToBudBurstStage
    };
};
/**
 * Hook to handle Tavmills activation and other automations
 */
export const useTavmillsActivation = ({ resource }) => {
    const client = useClient();
    const { t } = useIntl();
    const [activationStep, setActivationStep] = useState('presentation');
    const today = moment();
    const startOfTheYear = today.clone().startOf('y').unix().toString();
    const tavmillsOptionRequest = useQuery(client.options.get(OptionCode.tavmills, { organizationId: resource?.organizationId }), { enabled: !isNil(resource) && !isNil(resource.organizationId) });
    const tavmillsOption = tavmillsOptionRequest.data;
    const { isNotSelectable, missingMetrics, hasMissingMeasurements } = useCanActivateOptionOnResource({
        resource,
        option: tavmillsOption
    });
    const canActivateTavmills = !isNotSelectable;
    const missingMetricsListMessage = hasMissingMeasurements
        ? `${t('models.metric.missing_many')} ${missingMetrics
            .map((m) => {
            const key = `metrics.${m}`;
            // @ts-expect-error some keys combinations might not exist on wintl
            return t(key);
        })
            .join(', ')}`
        : undefined;
    const { lastCrop, hasNoActiveCrop, hasNoActiveCycle, isLoading: isCropTrackingLoading } = useCropTracking({
        plot: isPlot(resource) ? resource : undefined
    });
    const [createCycle, createCycleRequest] = useMutation(client.cycles.create(lastCrop?.id)(), { keysToInvalidate: [client.plots.get(resource?.id).invalidationKey] });
    const [createCrop, createCropRequest] = useMutation(client.crops.create(), {
        onSuccess: (data) => createCycle({ cropId: data.id, startedAt: startOfTheYear })
    });
    const handleActivationSuccess = useCallback(() => {
        setActivationStep('configuration');
        // Create an apple crop & start a new cycle on the first day of the year
        if (hasNoActiveCycle && hasNoActiveCrop && !isNil(resource)) {
            createCrop({ plotId: resource.id, seedId: APPLE_SEED_ID });
        }
    }, [createCrop, hasNoActiveCrop, hasNoActiveCycle, resource]);
    const [activateTavmills, activateTavmillsRequest] = useMutation(client.options.activate(OptionCode.tavmills, {
        organizationId: resource?.organizationId,
        stationId: isStation(resource) ? resource?.id : undefined,
        plotId: isPlot(resource) ? resource.id : undefined
    }), {
        onSuccess: handleActivationSuccess
    });
    const mergedRequests = mergeQueryState(tavmillsOptionRequest);
    const mergedMutations = mergeMutationState(activateTavmillsRequest, createCycleRequest, createCropRequest);
    const isAtLeastOneRequestFetching = isCropTrackingLoading || mergedRequests.isLoading || mergedMutations.isPending;
    return {
        activateTavmills,
        activateTavmillsRequest,
        activationStep,
        canActivateTavmills,
        isAtLeastOneRequestFetching,
        missingMetricsListMessage,
        tavmillsOption
    };
};
/** Color matching an infection level */
export const getLevelColor = (theme, level) => level === InfectionLevel.none
    ? theme.colors.data.good
    : level === InfectionLevel.light
        ? theme.colors.data.average
        : level === InfectionLevel.medium
            ? theme.colors.data.warn
            : level === InfectionLevel.severe
                ? theme.colors.data.danger
                : undefined;
const tickSize = 0.25;
export const ticksByInfectionLevel = {
    [InfectionLevel.none]: tickSize * 0,
    [InfectionLevel.light]: tickSize * 1,
    [InfectionLevel.medium]: tickSize * 2,
    [InfectionLevel.severe]: tickSize * 3
};
export const levels = ['none', 'light', 'medium', 'severe'];
